import {
    StarIcon,
    ChatTextIcon,
    ChartLineUpIcon,
    UsersIcon,
    TicketIcon,
    BuildingsIcon,
    ShoppingCartIcon,
    EnvelopeIcon,
    CalendarIcon,
    FileTextIcon,
    FolderIcon,
    DatabaseIcon,
} from '@phosphor-icons/react'
import { ScoopSpinner } from '../../../../../ui'
import styles from './PresetSelection.module.scss'

/**
 * Maps preset IDs to appropriate icons based on common patterns
 */
const getPresetIcon = (presetId) => {
    const id = presetId?.toLowerCase() || ''

    if (id.includes('sales') || id.includes('pipeline') || id.includes('deal')) {
        return ChartLineUpIcon
    }
    if (id.includes('contact') || id.includes('lead') || id.includes('people')) {
        return UsersIcon
    }
    if (id.includes('ticket') || id.includes('support') || id.includes('case')) {
        return TicketIcon
    }
    if (id.includes('company') || id.includes('account') || id.includes('organization')) {
        return BuildingsIcon
    }
    if (id.includes('order') || id.includes('product') || id.includes('commerce')) {
        return ShoppingCartIcon
    }
    if (id.includes('email') || id.includes('message') || id.includes('campaign')) {
        return EnvelopeIcon
    }
    if (id.includes('event') || id.includes('meeting') || id.includes('calendar')) {
        return CalendarIcon
    }
    if (id.includes('document') || id.includes('file') || id.includes('note')) {
        return FileTextIcon
    }
    if (id.includes('project') || id.includes('folder')) {
        return FolderIcon
    }
    // Default fallback
    return DatabaseIcon
}

/**
 * Displays preset selection as a row list
 * @param {Object} props
 * @param {Array} props.presets - Available presets from startExtractConversation
 * @param {string} props.selectedPreset - Currently selected preset ID
 * @param {Function} props.onSelectionChange - Called when selection changes (presetId)
 * @param {boolean} props.loading - Loading state
 */
export const PresetSelection = ({ presets = [], selectedPreset, onSelectionChange, loading }) => {
    const handlePresetClick = (presetId) => {
        if (loading) return
        onSelectionChange(presetId)
    }

    if (loading) {
        return (
            <div className={styles.loadingContainer}>
                <ScoopSpinner size={40} />
                <span className={styles.loadingText}>Analyzing your selection...</span>
            </div>
        )
    }

    return (
        <div className={styles.container}>
            <h3 className={styles.title}>What would you like to analyze?</h3>

            <div className={styles.list}>
                {presets.map((preset) => {
                    const PresetIcon = getPresetIcon(preset.id)
                    return (
                        <button
                            key={preset.id}
                            className={`${styles.presetRow} ${selectedPreset === preset.id ? styles.selected : ''}`}
                            onClick={() => handlePresetClick(preset.id)}
                            disabled={loading}
                        >
                            <div className={styles.iconContainer}>
                                <PresetIcon size={24} className={styles.presetIcon} />
                            </div>
                            <div className={styles.content}>
                                <span className={styles.presetLabel}>
                                    {preset.label}
                                    {preset.isDefault && (
                                        <StarIcon
                                            size={14}
                                            weight="fill"
                                            className={styles.starIcon}
                                        />
                                    )}
                                </span>
                                <span className={styles.presetDescription}>
                                    {preset.description}
                                </span>
                            </div>
                        </button>
                    )
                })}

                <button
                    className={`${styles.presetRow} ${styles.somethingElse} ${selectedPreset === 'something_else' ? styles.selected : ''}`}
                    onClick={() => handlePresetClick('something_else')}
                    disabled={loading}
                >
                    <div className={styles.iconContainer}>
                        <ChatTextIcon size={24} className={styles.chatIcon} />
                    </div>
                    <div className={styles.content}>
                        <span className={styles.presetLabel}>Something else...</span>
                        <span className={styles.presetDescription}>
                            Describe what you want to analyze
                        </span>
                    </div>
                </button>
            </div>
        </div>
    )
}
